import React from 'react';
import { Eye, Edit2, Trash2, ImageIcon, FileText, ClipboardList, Tag, X, CheckCircle, Link2 } from 'lucide-react';
import MultiSelectSearchFilter from '../../common/MultiSelectSearchFilter';

const TableView = ({
  items: rawItems,
  activeTab,
  onView,
  onEdit,
  onDelete,
  getThumbnailUrl,
  truncateText,
  getItemTitle,
  isReadOnly,
  availableTags,
  selectedTagFilters,
  onTagFiltersChange,
  availablePrograms,
  selectedProgramFilters,
  onProgramFiltersChange,
  selectedEventStatusFilters,
  onEventStatusFiltersChange,
  availableSubmissionForms,
  selectedSubmissionFormFilters,
  onSubmissionFormFiltersChange,
  selectedSubmissionStatusFilters,
  onSubmissionStatusFiltersChange,
  onCopyUrl,
  onStudentClick,
}) => {
  const items = Array.isArray(rawItems) ? rawItems : [];

  const getVisibleTagCount = (tags, maxWidth = 160) => {
    // Add null/undefined safety check
    if (!tags || !Array.isArray(tags) || tags.length === 0) {
      return 0;
    }

    const availableChars = Math.floor((maxWidth - 20) / 6);
    let usedChars = 0;
    let count = 0;

    for (const tag of tags) {
      const tagChars = tag.title.length + 3;
      if (usedChars + tagChars <= availableChars) {
        usedChars += tagChars;
        count++;
      } else {
        break;
      }
    }

    return Math.max(1, count);
  };

  const renderTableHeaders = () => {
    const headers = [];
    
    // Add Order column as first column for Events & Information
    if (activeTab === 'Events & Information') {
      headers.push({ key: 'order', label: 'Order' });
    }
    
    if (activeTab === 'Courses' || activeTab === 'Events & Information') {
      headers.push({ key: 'thumbnail', label: 'Thumbnail' });
    }
    
    headers.push({ key: 'title', label: activeTab === 'Questions' ? 'Question Text' : activeTab === 'Submissions' ? 'Student' : 'Title' });
    
    switch (activeTab) {
      case 'Lessons':
        headers.push({ key: 'tags', label: 'Tags' });
        break;
      case 'Courses':
        headers.push(
          { key: 'description', label: 'Description' },
          { key: 'content', label: 'Content' },
          { key: 'tags', label: 'Tags' }
        );
        break;
      case 'Paths':
        headers.push(
          { key: 'scoreRange', label: 'Score Range' },
          { key: 'courses', label: 'Courses' },
          { key: 'tags', label: 'Tags' }
        );
        break;
      case 'Questions':
        headers.push(
          { key: 'options', label: 'Options' },
          { key: 'tags', label: 'Tags' }
        );
        break;
      case 'Events & Information':
        headers.push(
          { key: 'type', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'program', label: 'Program' },
          { key: 'date', label: 'Date' }
        );
        break;
      case 'Submission Forms':
        headers.push(
          { key: 'course', label: 'Course' },
          { key: 'description', label: 'Description' },
          { key: 'requirements', label: 'Requirements' },
          { key: 'autoApproval', label: 'Auto Approval' },
          { key: 'repeatable', label: 'Repeatable' },
          { key: 'tags', label: 'Tags' }
        );
        break;
      case 'Submissions':
        headers.push(
          { key: 'program', label: 'Program' },
          { key: 'form', label: 'Form' },
          { key: 'status', label: 'Status' },
          { key: 'date', label: 'Submitted At' }
        );
        break;
      case 'Terms & Conditions':
        headers.push(
          { key: 'program', label: 'Program' },
          { key: 'content', label: 'Content Preview' }
        );
        break;
      case 'Reinforcements':
        headers.push(
          { key: 'content', label: 'Content' },
          { key: 'tags', label: 'Tags' }
        );
        break;
      case 'Tags':
        headers.push({ key: 'createdBy', label: 'Created By' });
        break;
    }
    
    headers.push({ key: 'actions', label: '' });
    
    return headers;
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto shadow-sm rounded-lg border border-gray-200">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {renderTableHeaders().map(header => (
                    <th
                      key={header.key}
                      scope="col"
                      className={`px-3 sm:px-4 lg:px-6 py-3 text-left text-xs font-medium text-[#054581] uppercase tracking-wider ${
                        header.key === 'thumbnail' ? 'w-20' :
                        header.key === 'order' ? 'w-16' :
                        header.key === 'title' ? (activeTab === 'Submissions' ? 'w-48 min-w-0' : 'w-1/3 min-w-0') :
                        header.key === 'description' ? 'w-1/4 min-w-0 hidden md:table-cell' :
                        header.key === 'content' ? 'w-24' :
                        header.key === 'tags' ? 'w-40 min-w-0' :
                        header.key === 'actions' ? (onCopyUrl ? 'w-48' : 'w-24') :
                        header.key === 'status' ? 'w-24' :
                        header.key === 'date' ? 'w-28 hidden sm:table-cell' :
                        header.key === 'program' ? 'w-48 min-w-0' :
                        header.key === 'requirements' ? 'w-32' :
                        header.key === 'autoApproval' ? 'w-20 hidden xl:table-cell' :
                        header.key === 'repeatable' ? 'w-20 hidden xl:table-cell' :
                        header.key === 'form' ? 'w-40' :
                        header.key === 'course' ? 'w-40' :
                        header.key === 'scoreRange' ? 'w-24' :
                        header.key === 'courses' ? 'w-20' :
                        header.key === 'options' ? 'w-20' :
                        header.key === 'type' ? 'w-24' :
                        header.key === 'createdBy' ? 'w-32' :
                        'w-auto'
                      }`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map(item => (
                <tr key={item._id} className="hover:bg-gray-50 transition-colors duration-150">
                  {/* Order column - first column for Events & Information */}
                  {activeTab === 'Events & Information' && (
                    <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {item.order || (
                          <span className="text-gray-400 italic">-</span>
                        )}
                      </div>
                    </td>
                  )}
                  {(activeTab === 'Courses' || activeTab === 'Events & Information') && (
                    <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                      {item.thumbnail ? (
                        <img 
                          src={getThumbnailUrl(item.thumbnail)}
                          alt={item.title}
                          className="w-16 h-16 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center"><ImageIcon class="w-8 h-8 text-gray-400" /></div>';
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-3 sm:px-4 lg:px-6 py-4">
                    <div className="flex items-center min-w-0">
                      {activeTab === 'Tags' && (
                        <div className="mr-3 flex-shrink-0">
                          <Tag className="w-4 h-4 text-[#1086a4]" />
                        </div>
                      )}
                      {activeTab === 'Submissions' && item.userId?._id && onStudentClick ? (
                        <button
                          onClick={() => onStudentClick(item.userId._id)}
                          className="text-sm font-medium text-[#1086a4] hover:text-[#054581] hover:underline truncate min-w-0 text-left transition-colors"
                        >
                          {getItemTitle(item)}
                        </button>
                      ) : (
                        <div className="text-sm font-medium text-gray-900 truncate min-w-0">{getItemTitle(item)}</div>
                      )}
                    </div>
                  </td>
                  {activeTab === 'Lessons' && (
                    <td className="px-3 sm:px-4 lg:px-6 py-4">
                      <div className="flex flex-wrap gap-1 overflow-hidden">
                        {item.tags && item.tags.length > 0 ? (
                          <>
                            {(() => {
                              const visibleCount = getVisibleTagCount(item.tags);
                              return item.tags.slice(0, visibleCount).map(tag => (
                                <span
                                  key={tag._id}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4] truncate"
                                  title={tag.title}
                                >
                                  <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                                  <span className="truncate">{tag.title}</span>
                                </span>
                              ));
                            })()}
                            {(() => {
                              const visibleCount = getVisibleTagCount(item.tags);
                              return item.tags.length > visibleCount && (
                                <span
                                  className="text-xs text-gray-500 cursor-help"
                                  title={item.tags.slice(visibleCount).map(tag => tag.title).join(', ')}
                                >
                                  +{item.tags.length - visibleCount}
                                </span>
                              );
                            })()}
                          </>
                        ) : (
                          <span className="text-xs text-gray-500">No tags</span>
                        )}
                      </div>
                    </td>
                  )}
                  {activeTab === 'Courses' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 hidden md:table-cell">
                        <div className="text-sm text-gray-900 truncate" title={item.description}>
                          {truncateText(item.description, 60)}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1">
                              <FileText className="h-4 w-4 text-[#1086a4]" />
                              {item.lessonsCount ?? item.lessons?.length ?? 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <ClipboardList className="h-4 w-4 text-[#1086a4]" />
                              {item.submissionFormsCount ?? item.submissionForms?.length ?? 0}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="flex flex-wrap gap-1 overflow-hidden">
                          {item.tags && item.tags.length > 0 ? (
                            <>
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.slice(0, visibleCount).map(tag => (
                                  <span
                                    key={tag._id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4] truncate"
                                    title={tag.title}
                                  >
                                    <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                                    <span className="truncate">{tag.title}</span>
                                  </span>
                                ));
                              })()}
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.length > visibleCount && (
                                  <span
                                    className="text-xs text-gray-500 cursor-help"
                                    title={item.tags.slice(visibleCount).map(tag => tag.title).join(', ')}
                                  >
                                    +{item.tags.length - visibleCount}
                                  </span>
                                );
                              })()}
                            </>
                          ) : (
                            <span className="text-xs text-gray-500">No tags</span>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Paths' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.rangeMin} - {item.rangeMax}</div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.coursesCount ?? item.courses?.length ?? 0}</div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="flex flex-wrap gap-1 overflow-hidden">
                          {item.tags && item.tags.length > 0 ? (
                            <>
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.slice(0, visibleCount).map(tag => (
                                  <span
                                    key={tag._id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4] truncate"
                                    title={tag.title}
                                  >
                                    <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                                    <span className="truncate">{tag.title}</span>
                                  </span>
                                ));
                              })()}
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.length > visibleCount && (
                                  <span
                                    className="text-xs text-gray-500 cursor-help"
                                    title={item.tags.slice(visibleCount).map(tag => tag.title).join(', ')}
                                  >
                                    +{item.tags.length - visibleCount}
                                  </span>
                                );
                              })()}
                            </>
                          ) : (
                            <span className="text-xs text-gray-500">No tags</span>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Questions' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.options?.length || 0}</div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="flex flex-wrap gap-1 overflow-hidden">
                          {item.tags && item.tags.length > 0 ? (
                            <>
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.slice(0, visibleCount).map(tag => (
                                  <span
                                    key={tag._id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4] truncate"
                                    title={tag.title}
                                  >
                                    <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                                    <span className="truncate">{tag.title}</span>
                                  </span>
                                ));
                              })()}
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.length > visibleCount && (
                                  <span
                                    className="text-xs text-gray-500 cursor-help"
                                    title={item.tags.slice(visibleCount).map(tag => tag.title).join(', ')}
                                  >
                                    +{item.tags.length - visibleCount}
                                  </span>
                                );
                              })()}
                            </>
                          ) : (
                            <span className="text-xs text-gray-500">No tags</span>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Events & Information' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.type === 'event' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {item.type === 'event' ? 'Event' : 'Information'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          item.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {item.isActive !== false ? 'Active' : 'Deactivated'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-sm text-gray-900 truncate max-w-32" title={item.program?.title || 'N/A'}>
                          {item.program?.title || 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Submission Forms' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="text-sm text-gray-900 truncate" title={item.courseId?.title || 'No Course Assigned'}>
                          {item.courseId?.title || 'No Course Assigned'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 hidden lg:table-cell">
                        <div 
                          className="text-sm text-gray-900 max-w-xs truncate" 
                          dangerouslySetInnerHTML={{ __html: truncateText(item.description, 60) }}
                        />
                      </td>
                      <td className="px-3 py-4 lg:px-6">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            const requirements = [];
                            if (item.linkRequired) requirements.push('Link');
                            if (item.commentRequired) requirements.push('Comment');
                            if (item.achievementImageRequired) requirements.push('Achievement Image');
                            return requirements.length > 0 ? requirements.join(' + ') : 'None';
                          })()}
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap hidden xl:table-cell">
                        <div className="text-sm text-gray-900">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.automaticApproval ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.automaticApproval ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap hidden xl:table-cell">
                        <div className="text-sm text-gray-900">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.repeatable ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.repeatable ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-4 lg:px-6">
                        <div className="flex flex-wrap gap-1 overflow-hidden">
                          {item.tags && item.tags.length > 0 ? (
                            <>
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.slice(0, visibleCount).map(tag => (
                                  <span
                                    key={tag._id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4] truncate"
                                    title={tag.title}
                                  >
                                    <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                                    <span className="truncate">{tag.title}</span>
                                  </span>
                                ));
                              })()}
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.length > visibleCount && (
                                  <span
                                    className="text-xs text-gray-500 cursor-help"
                                    title={item.tags.slice(visibleCount).map(tag => tag.title).join(', ')}
                                  >
                                    +{item.tags.length - visibleCount}
                                  </span>
                                );
                              })()}
                            </>
                          ) : (
                            <span className="text-xs text-gray-500">No tags</span>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Submissions' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="text-sm text-gray-900 truncate" title={item.userId?.group?.program?.title || 'N/A'}>
                          {item.userId?.group?.program?.title || 'N/A'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="text-sm text-gray-900 truncate" title={item.submissionFormId?.title || 'Unknown Form'}>
                          {item.submissionFormId?.title || 'Unknown Form'}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            item.status === 'approved' ? 'bg-green-100 text-green-800' :
                            item.status === 'more_info_needed' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.status === 'more_info_needed' ? 'More Info Needed' : item.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(item.submittedAt || item.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Terms & Conditions' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {item.program?.title || <span className="text-gray-500">No program</span>}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {item.contentPreview || truncateText(item.content, 120)}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Reinforcements' && (
                    <>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="text-sm text-gray-900 truncate"
                             title={item.content?.replace(/<[^>]*>/g, '')}>
                          {truncateText(item.content?.replace(/<[^>]*>/g, ''), 100)}
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 lg:px-6 py-4">
                        <div className="flex flex-wrap gap-1 overflow-hidden">
                          {item.tags && item.tags.length > 0 ? (
                            <>
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.slice(0, visibleCount).map(tag => (
                                  <span
                                    key={tag._id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4] truncate"
                                    title={tag.title}
                                  >
                                    <Tag className="w-3 h-3 mr-1 flex-shrink-0" />
                                    <span className="truncate">{tag.title}</span>
                                  </span>
                                ));
                              })()}
                              {(() => {
                                const visibleCount = getVisibleTagCount(item.tags);
                                return item.tags.length > visibleCount && (
                                  <span
                                    className="text-xs text-gray-500 cursor-help"
                                    title={item.tags.slice(visibleCount).map(tag => tag.title).join(', ')}
                                  >
                                    +{item.tags.length - visibleCount}
                                  </span>
                                );
                              })()}
                            </>
                          ) : (
                            <span className="text-xs text-gray-500">No tags</span>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                  {activeTab === 'Tags' && (
                    <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.createdBy}</div>
                    </td>
                  )}
                  <td className="px-3 sm:px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2 sm:gap-3">
                      {onCopyUrl && (activeTab === 'Submission Forms' || item.slug) && (
                        <button
                          onClick={() => onCopyUrl(item)}
                          className="text-[#1086a4] hover:text-[#054581] transition-colors p-1"
                          title="Copy student URL"
                        >
                          <Link2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onView(item)}
                        className="text-[#1086a4] hover:text-[#054581] transition-colors p-1"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!isReadOnly && (
                        <>
                          <button
                            onClick={() => onEdit(item)}
                            className="text-[#1086a4] hover:text-[#054581] transition-colors p-1"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => onDelete(item)}
                            className="text-red-500 hover:text-red-700 transition-colors p-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TableView; 